/**
 * @file        src/components/SupplierBalances.jsx
 * @description The supplier (TripJack) account position, as TripJack's own
 *              dashboard states it: Deposit Balance, Credit Balance and Dues.
 *
 *              ONE surface since 2026-09-25: the Topbar pill's popover. Reads
 *              the shared useWalletBalance() query, so it costs no request of
 *              its own — the Cash Balance KPI on the Dashboard is the same
 *              fetch.
 *
 *              A figure the supplier did not report renders "Unavailable", not
 *              "₹0.00": an account with an empty deposit and a live credit line
 *              genuinely reads ₹0.00, and the two must stay distinguishable.
 * @author      Kinetika Labs
 * @created     2026-09-21
 * @changelog
 *   - 2026-09-25 Claude — Dropped the 'strip' variant with the Dashboard panel
 *                          that was its only caller (see Dashboard.jsx). The
 *                          popover layout is the only one now, so it is not a
 *                          choice any more.
 *   - 2026-09-24 Claude — Print a one-line note when the supplier reports no
 *                          credit facility: Credit Balance and Dues then read
 *                          ₹0.00 (a real figure) rather than "Unavailable".
 *   - 2026-09-21 Claude — Initial. The admin showed one supplier number where
 *                          TripJack shows three, so an account running on
 *                          credit looked like an empty one.
 */
import { useWalletBalance } from '../lib/useWalletBalance';

/**
 * Deposit is money already with the supplier; credit is headroom still to draw;
 * dues is what has been drawn and is owed back. `tone` only colours the value —
 * dues is the one figure where "larger" is not "better".
 */
const CELLS = [
  { key: 'deposit', label: 'Deposit Balance', hint: 'Prepaid funds held with the supplier' },
  { key: 'credit', label: 'Credit Balance', hint: 'Credit still available to draw' },
  { key: 'dues', label: 'Dues', hint: 'Credit drawn and owed back', tone: 'due' },
];

/** The compact list under the Topbar Cash Balance pill. */
export default function SupplierBalances() {
  const wallet = useWalletBalance();

  return (
    <div className="sup-bal sup-bal-popover">
      {CELLS.map(({ key, label, hint, tone }) => (
        <div className="sup-bal-cell" key={key} title={hint}>
          <small>{label}</small>
          {wallet.isLoading
            ? <span className="skeleton" style={{ display: 'block', width: 92, height: 16, marginTop: 4 }} />
            : <b className={tone === 'due' && wallet[key].value > 0 ? 'due' : undefined}>{wallet[key].display}</b>}
        </div>
      ))}
      {/* The two credit cells read ₹0.00 because the supplier states no credit
          facility on this account — a real answer, and the reader should not
          have to wonder whether a line was silently dropped. */}
      {!wallet.isLoading && wallet.noCreditLine && (
        <p className="sup-bal-note">No credit line on this account — bookings draw on the deposit.</p>
      )}
    </div>
  );
}
